/**
 * Pre-compare time estimate (fu4 sub-task 13, Trello #748).
 *
 * TWO terms, not one, because a Compare run has two per-contact costs and they
 * differ by ~7x. `compare_matching.match_all` resolves every contact whose
 * canonical LinkedIn URL comes back from the chunked `in` query IN MEMORY (a
 * handful of HTTP calls for the whole import — effectively free per contact),
 * and sends everything it could not resolve that way through a THROTTLED
 * per-contact fallback probe. The repo's own calibration script says this in
 * as many words: the fallback "DOMINATES timing at any meaningful rate … a
 * single constant fit to the batched path underestimates those"
 * (`tests/e2e/fixtures/imports/calibrate_compare_timing.py`).
 *
 * So the fix for the estimate being wrong is not a better coefficient — it is
 * the second term. Re-measuring the batched constant on dev would leave the
 * error exactly where it was.
 */

/**
 * Seconds per contact the batched `in` path resolves in memory.
 *
 * MEASURED: ~10 s for a 690-contact list on the LOCAL stack, built so every
 * row resolves on the batched path (≈0.0145 s/contact). This is the number
 * sub-task 12's harness re-measures; a dev re-run updates THIS line only.
 */
export const COMPARE_SECONDS_PER_BATCHED_CONTACT = 10 / 690;

/**
 * Seconds per contact that misses the batch and pays a fallback probe.
 *
 * DERIVED, not measured, and deliberately so — it is a throttle, so measuring
 * it would only re-discover the configured rate. `match_all` fans the fallback
 * probes out under a `TokenBucket(rate=_DEFAULT_RATE)` with `_DEFAULT_RATE =
 * 10.0` tokens/s (`nu_assist_shared/compare_matching.py`), and its `_probe`
 * takes ONE token per fallback CONTACT — not per HTTP call, so the up-to-three
 * probes a genuinely-unknown contact makes still cost one token. Steady-state
 * throughput is therefore 10 contacts/s ⇒ 0.1 s/contact.
 *
 * If that rate changes, this constant is wrong; it is not independently
 * calibratable.
 */
export const COMPARE_SECONDS_PER_FALLBACK_CONTACT = 1 / 10;

/**
 * Wall-clock seconds for a run over `contactCount` contacts of which
 * `fallbackCount` are expected to miss the batched path.
 *
 * `fallbackCount` is clamped into `[0, contactCount]`: callers derive it from
 * WHOLE-IMPORT counts, so a selection can legitimately be handed a share
 * larger than itself.
 *
 * Not modelled: the conflict-avatar fan-out in `compare_worker` (also 10/s,
 * but only for W1/W2/W3 conflict rows, whose count tracks source-data hygiene
 * rather than list size) and the 2 s poll quantization the UI adds on top.
 * Both push the real number UP, which keeps this estimate on the honest side
 * of the promise.
 */
export function estimateCompareSeconds(contactCount: number, fallbackCount: number): number {
  const fallback = Math.min(Math.max(0, fallbackCount), Math.max(0, contactCount));
  const batched = Math.max(0, contactCount) - fallback;
  return (
    fallback * COMPARE_SECONDS_PER_FALLBACK_CONTACT +
    batched * COMPARE_SECONDS_PER_BATCHED_CONTACT
  );
}

/**
 * Coarse human estimate: "~N sec" under a minute, "~N min" (rounded, never
 * below 1) from there. Rounded rather than ceil'd for minutes so 61 s reads
 * "~1 min", not "~2 min" — the "~" already owns the imprecision. Never below
 * "~1 sec": a zero-second promise reads as broken, not fast.
 *
 * `fallbackCount` has no default on purpose. The one-argument version of this
 * function silently charged the batched rate for every contact, which is how
 * it came to promise "~10 sec" for a first compare that takes over a minute;
 * a caller must now state what it believes the composition to be.
 */
export function formatCompareEstimate(contactCount: number, fallbackCount: number): string {
  const seconds = Math.max(1, Math.ceil(estimateCompareSeconds(contactCount, fallbackCount)));
  if (seconds < 60) return `~${seconds} sec`;
  return `~${Math.max(1, Math.round(seconds / 60))} min`;
}
